import { Flags } from '@constants/flags';
import { BulkTraderBreadcrumb as BulkTraderRevampBreadcrumb } from '@containers/BulkTraderDetail/bulkTraderBreadcrumb';
import { Layout } from '@containers/Layout';
import { Pages } from '@generated/graphql';
import ErrorBoundary from '@ovComponents/3-pattern/errorBoundary/errorBoundary';
import { BulkRebalanceRouteGuard } from '@ovComponents/4-module/bulkRebalance';
import { AccountBreadcrumb } from '@pages/AccountDetail/accountBreadcrumb';
import { CustomReportBreadcrumb } from '@pages/AccountDetail/ReportDetail/customReportBreadcrumb';
import { AccountGroupBreadcrumb } from '@pages/AccountGroupDetail/accountGroupBreadcrumb';
import { FeeReportBreadcrumb } from '@pages/BillingCycleDetail/feeReportBreadcrumb';
import { PortfolioModelBreadcrumb } from '@pages/BuildModels/portfolioModelBreadcrumb';
import { BulkImportBreadcrumb } from '@pages/BulkImportDetail/bulkImportBreadcrumb';
import { ImportFileBreadcrumb } from '@pages/BulkImportFileDetail/importFileBreadcrumb';
import { BulkRebalanceBreadcrumb } from '@pages/BulkRebalanceDetail/bulkRebalanceBreadcrumb';
import { BulkUpsertItemsBreadcrumb } from '@pages/BulkUpsertRequestDetail/bulkUpsertItemsBreadcrumb';
import { ClientBreadcrumb } from '@pages/ClientDetail/clientBreadcrumb';
import { EntityBreadcrumb } from '@pages/ClientDetail/entityBreadcrumb';
import { ClientReportBreadcrumb } from '@pages/ClientReportDetail/clientReportBreadcrumb';
import { ConfigurableTablePageBreadcrumb } from '@pages/ConfigurableTableDetail/configurableTablePageBreadcrumb';
import { DailyFeeDetailsBreadcrumb } from '@pages/DailyFeeDetail/dailyFeeDetailsBreadcrumb';
import { DashboardBreadcrumb } from '@pages/DashboardDetail/dashboardBreadcrumb';
import { FormTemplateBreadcrumb } from '@pages/FormTemplateDetail/formTemplateBreadcrumb';
import { GoalBreadcrumb } from '@pages/GoalDetail/goalBreadcrumb';
import { ClientGroupBreadcrumb } from '@pages/HouseholdDetail/clientGroupBreadcrumb';
import { BulkTraderBreadcrumb } from '@pages/LegacyBulkTraderDetail/bulkTraderBreadcrumb';
import { OrgDetailsBreadcrumb } from '@pages/OrgDetail/orgDetailsBreadcrumb';
import { PortfolioOptimizerBreadcrumb } from '@pages/PortfolioOptimizerDetail/portfolioOptimizerBreadcrumb';
import { RebalanceBreadcrumb } from '@pages/RebalanceDetail/rebalanceBreadcrumb';
import { AccountSnapshotBreadcrumb } from '@pages/ReconciliationV2Detail/accountSnapshotBreadcrumb';
import { AllDocumentsBreadcrumb } from '@pages/StaticDocumentList/allDocumentsBreadcrumb';
import { SubAccountBreadcrumb } from '@pages/SubAccountDetail/subAccountBreadcrumb';
import { AlertBannerBreadcrumb } from '@pages/setting/AlertBannerDetail/alertBannerBreadcrumb';
import { BillingScheduleBreadcrumb } from '@pages/setting/BillingScheduleDetail/billingScheduleBreadcrumb';
import { ClientReportTemplateBreadcrumb } from '@pages/setting/ClientReportTemplateDetail/clientReportTemplateBreadcrumb';
import { CustodianConnectionBreadcrumb } from '@pages/setting/CustodianConnectionDetail/custodianConnectionBreadcrumb';
import { FeeGridBreadcrumb } from '@pages/setting/FeeGridDetail/feeGridBreadcrumb';
import { HomePageConfigurationBreadcrumb } from '@pages/setting/HomePageConfigurationDetail/homePageConfigurationBreadcrumb';
import { PageConfigurationBreadcrumb } from '@pages/setting/PageConfigurationDetail/pageConfigurationBreadcrumb';
import { StaticPageWidgetConfigurationBreadcrumb } from '@pages/setting/StaticPageWidgetConfigurationDetail/staticPageWidgetConfigurationBreadcrumb';
import { WorkflowBreadcrumb } from '@pages/setting/WorkflowDetail/workflowBreadcrumb';
import type { AppRoute } from '@utils/filterEnabledRoutes';
import { createElement, type ReactNode } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router';

export const ROUTE_FLAGS = [
  { flag: Flags.advisor_contact_repcode, options: { default: false } },
  { flag: Flags.alert_banners, options: { default: false } },
  { flag: Flags.bulk_trader_revamp, options: { default: false } },
  { flag: Flags.mackenzie_release_3, options: { default: false } },
  {
    flag: Flags.portfolio_trading_prototype,
    options: { default: false },
  },
  {
    flag: Flags.recon_activities_dashboard,
    options: { default: false },
  },
  { flag: Flags.recon_flags_dashboard, options: { default: false } },
  { flag: Flags.recon_tolerance_config, options: { default: false } },
  { flag: Flags.siem_data_feed, options: { default: false } },
  { flag: Flags.text_variants, options: { default: false } },
] as const;

const lazyComponentShowcase = () => import('@pages/ComponentShowcase');
const lazyAccountGroups = () => import('@pages/AccountGroupList');
const lazyAccounts = () => import('@pages/AccountList');
const lazyAccountsReview = () => import('@pages/AccountReview');
const lazyAdvisorContactAdmin = () => import('@pages/AdvisorContact');
const lazyAllNewsAndInsights = () => import('@pages/AllNewsAndInsightList');
const lazyAllTransactions = () => import('@pages/AllTransactionList');
const lazyAnnualSuitabilityReview = () =>
  import('@pages/AnnualSuitabilityReview');
const lazyAssetClasses = () => import('@pages/AssetClassList');
const lazyBankAccountsToReview = () => import('@pages/BankAccountReview');
const lazyBillingManagement = () => import('@pages/BillingCycleList');
const lazyBulkUpsertItems = () => import('@pages/BulkUpsertRequestDetail');
const lazyBulkUpsertRequests = () => import('@pages/BulkUpsertRequestList');
const lazyClientReportTemplate = () =>
  import('@pages/setting/ClientReportTemplateDetail');
const lazyComplianceReview = () => import('@pages/ComplianceReview');
const lazyConfigurableTablePage = () =>
  import('@pages/ConfigurableTableDetail');
const lazyConfigureWorkflowPage = () => import('@pages/setting/WorkflowDetail');
const lazyDailyFeeDetails = () => import('@pages/DailyFeeDetail');
const lazyDashboard = () => import('@pages/DashboardDetail');
const lazyEditFormTemplate = () => import('@pages/FormTemplateDetail');
const lazyEditDashboard = () => import('@pages/setting/EditDashboard');
const lazyElderlyClients = () => import('@pages/ElderlyClientReview');
const lazyFeeReport = () => import('@pages/BillingCycleDetail');
const lazyHouseholds = () => import('@pages/HouseholdList');
const lazyIndividuals = () => import('@pages/IndividualList');
const lazyInstitutionsToReview = () => import('@pages/InstitutionReview');
const lazyManageNewsAndInsight = () =>
  import('@pages/ManageNewsAndInsightList');
const lazyModelFamilies = () => import('@pages/ModelFamilyList');
const lazyModelPortfolioDetails = () => import('@pages/ModelPortfolioDetail');
const lazyModelPortfolios = () => import('@pages/ModelPortfolioList');
const lazyNewReport = () => import('@pages/AccountDetail/CreateReport');
const lazyNonIndividuals = () => import('@pages/NonIndividualList');
const lazyPageConfiguration = () =>
  import('@pages/setting/PageConfigurationDetail');
const lazyPortfolioTemplates = () => import('@pages/PortfolioTemplateList');
const lazyProductShelfPage = () => import('@pages/ProductShelfList');
const lazyRebalanceReport = () => import('@pages/RebalanceReport');
const lazyReconciliation = () => import('@pages/Reconciliation');
const lazyAccountSnapshot = () => import('@pages/ReconciliationV2Detail');
const lazyReconciliationV2 = () => import('@pages/ReconciliationV2List');
const lazyReports = () => import('@pages/AccountDetail/ReportList');
const lazyStatementsReview = () => import('@pages/StatementReview');
const lazyStaticPageWidgetConfiguration = () =>
  import('@pages/setting/StaticPageWidgetConfigurationDetail');
const lazyStaticDocumentsPage = () => import('@pages/StaticDocumentList');
const lazyTransactionMonitoring = () => import('@pages/TransactionMonitoring');
const lazyViewClientReport = () => import('@pages/ClientReportDetail');
const lazyViewPage = () => import('@pages/ViewDetail');
const lazyViewReport = () => import('@pages/AccountDetail/ReportDetail');
const lazyWorkflows = () => import('@pages/WorkflowList');
const lazyAccessSettings = () => import('@pages/setting/Access');
const lazyAccessUsersSettings = () => import('@pages/setting/AccessUserList');
const lazyAccountSettings = () => import('@pages/setting/Account');
const lazyAccountGroupSettings = () => import('@pages/setting/AccountGroup');
const lazyAccountsTypesSettings = () => import('@pages/setting/AccountType');
const lazyEditAlertBanner = () => import('@pages/setting/AlertBannerDetail');
const lazyNewAlertBanner = () => import('@pages/setting/CreateAlertBanner');
const lazyApiTokensSettings = () => import('@pages/setting/ApiTokenList');
const lazyAuthenticationSettings = () =>
  import('@pages/setting/Authentication');
const lazyAuthenticationDesignerSettings = () =>
  import('@pages/setting/AuthenticationDesigner');
const lazyBillingSchedulesSettings = () =>
  import('@pages/setting/BillingScheduleList');
const lazySalesTaxSettings = () => import('@pages/setting/SalesTax');
const lazyBillingSchedulePage = () =>
  import('@pages/setting/BillingScheduleDetail');
const lazyCreateBillingSchedulePage = () =>
  import('@pages/setting/CreateBillingSchedule');
const lazyBulkImportsSettings = () => import('@pages/setting/BulkImportList');
const lazyBulkImportFileLineItemsSettings = () =>
  import('@pages/setting/BulkImportFileDetail');
const lazyBulkImportFilesSettings = () =>
  import('@pages/setting/BulkImportDetail');
const lazyClientReports = () => import('@pages/setting/ClientReportList');
const lazyComplianceSettings = () => import('@pages/setting/Compliance');
const lazyConfigurableTablePageEditor = () =>
  import('@pages/setting/ConfigurableTableDetail');
const lazyConfigurableTablePagesSettings = () =>
  import('@pages/setting/ConfigurableTableList');
const lazyCustodianConnectionDetail = () =>
  import('@pages/setting/CustodianConnectionDetail');
const lazyCustodianConnectionsSettings = () =>
  import('@pages/setting/CustodianConnectionList');
const lazyCustomAttributeLabelsSettings = () =>
  import('@pages/setting/CustomAttributeLabelList');
const lazyCustomFieldsSettings = () => import('@pages/setting/CustomFieldList');
const lazyDashboards = () => import('@pages/setting/DashboardList');
const lazyDesignerSettings = () => import('@pages/setting/Designer');
const lazyDowntimeSchedulesSettings = () =>
  import('@pages/setting/DowntimeScheduleList');
const lazyEmbeddedSettings = () => import('@pages/setting/Embedded');
const lazyErrorPageDesignerSettings = () =>
  import('@pages/setting/ErrorPageDesigner');
const lazyFeatureFlagsSettings = () => import('@pages/setting/FeatureFlag');
const lazyFeeGrids = () => import('@pages/setting/FeeGridList/app');
const lazyFeeGridsSettings = () => import('@pages/setting/FeeGridList');
const lazyFeeGridPage = () => import('@pages/setting/FeeGridDetail');
const lazyCreateFeeGridPage = () => import('@pages/setting/CreateFeeGrid');
const lazyFeeSettings = () => import('@pages/setting/Fee');
const lazyFormDefinitionsSettings = () =>
  import('@pages/setting/FormDefinitionList');
const lazyForms = () => import('@pages/setting/FormList');
const lazyGeneralSettings = () => import('@pages/setting/General');
const lazyGeneralProductExperienceSettings = () =>
  import('@pages/setting/GeneralProductExperience');
const lazyHomePageConfigurationEditor = () =>
  import('@pages/setting/HomePageConfigurationDetail');
const lazyHomePageConfigurationsSettings = () =>
  import('@pages/setting/HomePageConfigurationList');
const lazyIntegrationSettings = () => import('@pages/setting/IntegrationList');
const lazyLocalizationSettings = () => import('@pages/setting/Localization');
const lazyLocalizationConfigurationSettings = () =>
  import('@pages/setting/LocalizationConfigurationList');
const lazyNewsAndInsights = () => import('@pages/setting/NewsAndInsightList');
const lazyNotifications = () => import('@pages/setting/NotificationList');
const lazyPageConfigurationsSettings = () =>
  import('@pages/setting/PageConfigurationList');
const lazyPortfolioManagementSettings = () =>
  import('@pages/setting/PortfolioManagement');
const lazyRepCodesSettings = () => import('@pages/setting/RepCodeList');
const lazyRoleProfilesSettings = () => import('@pages/setting/RoleProfileList');
const lazyRoleSettings = () => import('@pages/setting/Role');
const lazyRolesSettings = () => import('@pages/setting/RoleList');
const lazyStatementsAndTaxDocumentsSettings = () =>
  import('@pages/setting/StatementsAndTaxDocument');
const lazyStaticPageWidgetConfigurationsSettings = () =>
  import('@pages/setting/StaticPageWidgetConfigurationList');
const lazyTaxRankingsSettings = () => import('@pages/setting/TaxRanking');
const lazyTradesSettings = () => import('@pages/setting/Trade');
const lazyTradingSchedulesSettings = () =>
  import('@pages/setting/TradingScheduleList');
const lazyTransfersSettings = () => import('@pages/setting/Transfer');
const lazyWebhooksSettings = () => import('@pages/setting/WebhookList');
const lazyWorkflowsSettings = () => import('@pages/setting/WorkflowList');
const lazyAccountGroupsDetail = () => import('@pages/AccountGroupDetail');
const lazyAccountsDetail = () => import('@pages/AccountDetail');
const lazyBillingSchedules = () =>
  import('@pages/setting/BillingScheduleList/app');
const lazyBuildModels = () => import('@pages/BuildModels');
const lazyBulkImportFileLineItems = () => import('@pages/BulkImportFileDetail');
const lazyBulkImport = () => import('@pages/BulkImportDetail');
const lazyBulkImports = () => import('@pages/BulkImportList');
const lazyClientV2 = () => import('@pages/ClientDetail');
const lazyDevSettings = () => import('@pages/DevSettings');
const lazyGoalsDetail = () => import('@pages/GoalDetail');
const lazyHouseHold = () => import('@pages/HouseholdDetail');
const lazyBulkTrader = () => import('@pages/LegacyBulkTraderDetail');
const lazyBulkTraders = () => import('@pages/LegacyBulkTraderList');
const lazyOrgDetails = () => import('@pages/OrgDetail');
const lazyOrgSettings = () => import('@pages/OrgSettings');
const lazyPortfolioOptimizer = () => import('@pages/PortfolioOptimizerDetail');
const lazyPortfolioOptimizers = () => import('@pages/PortfolioOptimizerList');
const lazyRebalance = () => import('@pages/RebalanceDetail');
const lazyRebalances = () => import('@pages/RebalanceList');
const lazyRefinitivShowcase = () => import('@pages/RefinitivShowcase');
const lazySchedules = () => import('@pages/setting/TradingScheduleList/app');
const lazySubAccountDetail = () => import('@pages/SubAccountDetail');
const lazyFormMapper = () => import('@pages/FormMapper');
const lazyHomePage = () => import('@pages/HomePage');
const lazyPortfolioTradingHub = () => import('@pages/PortfolioTradingHub');
const lazyPortfolioTradingHubV2 = () => import('@pages/PortfolioTradingHubV2');
const lazyBulkRebalance = () => import('@pages/BulkRebalanceList');
const lazyBulkRebalanceDetail = () => import('@pages/BulkRebalanceDetail');
const lazyBulkTradersRevamp = () => import('@pages/BulkTraderList');
const lazyBulkTraderRevamp = () =>
  import('@ovComponents/4-module/bulkTrader/bulkTrader/bulkTrader').then(
    (mod) => ({ Component: mod.default }),
  );
const lazyReconActivities = () => import('@pages/ReconActivities');
const lazyReconFlags = () => import('@pages/ReconFlags');
const lazyFlagGlossary = () => import('@pages/FlagGlossaryList');
const lazyAlertBanners = () => import('@pages/setting/AlertBannerList');
const lazyTextDesignerSettings = () => import('@pages/setting/TextDesigner');
const lazyFlagPoliciesSettings = () => import('@pages/setting/FlagPolicy');
const lazyReconToleranceSettings = () =>
  import('@pages/setting/ReconTolerance');
const lazySiemDataFeedSettings = () =>
  import('@pages/setting/SiemDataFeedList');

const navigateTo = (to: string) =>
  createElement(Navigate, { to, replace: true });

const newsAndInsightsRoutes = () =>
  ({
    children: [
      {
        path: 'newsAndInsights/:widgetId',
        lazy: lazyAllNewsAndInsights,
        handle: { breadcrumb: 'navMenu:menu.newsAndInsights' },
      },
    ],
  }) satisfies AppRoute;

const clientReportRoutes = () =>
  ({
    children: [
      {
        path: 'clientReport/:clientReportTemplateId/:objectId',
        lazy: lazyViewClientReport,
        handle: { breadcrumb: ClientReportBreadcrumb },
      },
    ],
  }) satisfies AppRoute;

const subAccountRoutes = () =>
  ({
    path: 'subaccount/:subAccountId',
    handle: { breadcrumb: SubAccountBreadcrumb },
    children: [
      { index: true, lazy: lazySubAccountDetail },
      clientReportRoutes(),
    ],
  }) satisfies AppRoute;

const accountRoutes = () =>
  ({
    path: 'account/:accountId',
    handle: { breadcrumb: AccountBreadcrumb },
    children: [
      { index: true, lazy: lazyAccountsDetail },
      clientReportRoutes(),
      subAccountRoutes(),
    ],
  }) satisfies AppRoute;

const accountGroupRoutes = () =>
  ({
    path: 'accountGroup/:accountGroupId',
    handle: { breadcrumb: AccountGroupBreadcrumb },
    children: [
      { index: true, lazy: lazyAccountGroupsDetail },
      clientReportRoutes(),
      accountRoutes(),
    ],
  }) satisfies AppRoute;

const goalAccountAccountGroupSubaccountRoutes = () =>
  ({
    children: [
      {
        path: 'goal/:goalId',
        handle: { breadcrumb: GoalBreadcrumb },
        children: [
          { index: true, lazy: lazyGoalsDetail },
          clientReportRoutes(),
          subAccountRoutes(),
        ],
      },
      accountRoutes(),
      accountGroupRoutes(),
    ],
  }) satisfies AppRoute;

const householdRoutes = () =>
  ({
    children: [
      {
        path: 'household/:clientGroupId',
        handle: { breadcrumb: ClientGroupBreadcrumb },
        children: [
          { index: true, lazy: lazyHouseHold },
          clientReportRoutes(),
          goalAccountAccountGroupSubaccountRoutes(),
          newsAndInsightsRoutes(),
        ],
      },
    ],
  }) satisfies AppRoute;

// Role default view opens /households/:id without Pages.Households.
// Must not be Pages.Households — that would also mount the list index.
const HOUSEHOLD_DEFAULT_VIEW = 'householdDefaultView';

export const accessiblePagesForRoutes = (
  accessiblePages: Set<string>,
  isHouseholdDefaultView: boolean,
) => {
  if (!isHouseholdDefaultView || accessiblePages.has(Pages.Households)) {
    return accessiblePages;
  }
  const pages = new Set(accessiblePages);
  pages.add(HOUSEHOLD_DEFAULT_VIEW);
  return pages;
};

export const routeMap = [
  {
    path: '/',
    element: createElement(Layout),
    errorElement: createElement(ErrorBoundary),
    children: [
      { index: true, lazy: lazyHomePage },
      {
        path: 'dashboard/:dashboardId',
        lazy: lazyDashboard,
        handle: { breadcrumb: DashboardBreadcrumb },
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Home),
      },
      {
        path: 'productShelf',
        lazy: lazyProductShelfPage,
        handle: { breadcrumb: 'navMenu:menu.productShelfPage' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ProductShelf),
      },
      {
        path: 'assetClasses',
        lazy: lazyAssetClasses,
        handle: { breadcrumb: 'navMenu:menu.assetClassesPage' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AssetClasses),
      },
      {
        path: 'schedules',
        lazy: lazySchedules,
        handle: { breadcrumb: 'navMenu:menu.tradingSchedules' },
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Schedules),
      },
      {
        path: 'households',
        handle: { breadcrumb: 'navMenu:menu.households' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Households) ||
          accessiblePages.has(HOUSEHOLD_DEFAULT_VIEW),
        children: [
          {
            index: true,
            lazy: lazyHouseholds,
            enabled: ({ accessiblePages }) =>
              accessiblePages.has(Pages.Households),
          },
          {
            path: ':clientGroupId',
            handle: { breadcrumb: ClientGroupBreadcrumb },
            children: [
              { index: true, lazy: lazyHouseHold },
              clientReportRoutes(),
              accountGroupRoutes(),
              {
                enabled: ({ accessiblePages }) =>
                  accessiblePages.has(Pages.IndividualClients) ||
                  accessiblePages.has(Pages.NonIndividualClients),
                children: [
                  {
                    path: ':clients/:userId',
                    handle: { breadcrumb: ClientBreadcrumb },
                    children: [
                      { index: true, lazy: lazyClientV2 },
                      clientReportRoutes(),
                      goalAccountAccountGroupSubaccountRoutes(),
                      newsAndInsightsRoutes(),
                    ],
                  },
                  goalAccountAccountGroupSubaccountRoutes(),
                  newsAndInsightsRoutes(),
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'accounts',
        handle: { breadcrumb: 'navMenu:menu.accountsList' },
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Accounts),
        children: [{ index: true, lazy: lazyAccounts }],
      },
      {
        path: 'clients/:userId/account/:accountId',
        handle: { breadcrumb: AccountBreadcrumb },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Accounts) &&
          !accessiblePages.has(Pages.IndividualClients),
        children: [
          { index: true, lazy: lazyAccountsDetail },
          clientReportRoutes(),
        ],
      },
      {
        path: 'accountGroups',
        handle: { breadcrumb: 'navMenu:menu.accountGroupsList' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AccountGroups),
        children: [
          { index: true, lazy: lazyAccountGroups },
          {
            path: ':accountGroupId',
            handle: { breadcrumb: AccountGroupBreadcrumb },
            children: [
              { index: true, lazy: lazyAccountGroupsDetail },
              clientReportRoutes(),
              accountRoutes(),
            ],
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.IndividualClients),
        children: [
          {
            path: 'clients',
            handle: { breadcrumb: 'navMenu:menu.individuals' },
            children: [
              { index: true, lazy: lazyIndividuals },
              {
                path: ':userId',
                handle: { breadcrumb: ClientBreadcrumb },
                children: [
                  { index: true, lazy: lazyClientV2 },
                  clientReportRoutes(),
                  goalAccountAccountGroupSubaccountRoutes(),
                  householdRoutes(),
                  newsAndInsightsRoutes(),
                ],
              },
            ],
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.NonIndividualClients),
        children: [
          {
            path: 'nonIndividualClients',
            handle: { breadcrumb: 'navMenu:menu.nonIndividualClients' },
            children: [
              { index: true, lazy: lazyNonIndividuals },
              {
                path: ':userId',
                handle: { breadcrumb: EntityBreadcrumb },
                children: [
                  { index: true, lazy: lazyClientV2 },
                  clientReportRoutes(),
                  goalAccountAccountGroupSubaccountRoutes(),
                  newsAndInsightsRoutes(),
                ],
              },
            ],
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BuildModels),
        children: [
          {
            path: 'buildModels',
            handle: { breadcrumb: 'navMenu:menu.modelPortfolios' },
            children: [
              { index: true, lazy: lazyBuildModels },
              {
                path: ':id',
                lazy: lazyBuildModels,
                handle: { breadcrumb: PortfolioModelBreadcrumb },
              },
            ],
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioModelFamilies),
        children: [
          {
            path: 'modelFamilies',
            lazy: lazyModelFamilies,
            handle: { breadcrumb: 'navMenu:menu.modelFamily' },
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ModelPortfolios),
        children: [
          {
            path: 'modelPortfolios',
            handle: { breadcrumb: 'navMenu:menu.modelPortfolios' },
            children: [
              { index: true, lazy: lazyModelPortfolios },
              {
                path: ':id',
                lazy: lazyModelPortfolioDetails,
                handle: { breadcrumb: PortfolioModelBreadcrumb },
              },
            ],
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioTemplates),
        children: [
          {
            path: 'portfolioTemplates',
            lazy: lazyPortfolioTemplates,
            handle: { breadcrumb: 'navMenu:menu.portfolioTemplates' },
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Rebalancing),
        children: [
          {
            path: 'rebalances',
            handle: { breadcrumb: 'navMenu:menu.rebalancing' },
            children: [
              { index: true, lazy: lazyRebalances },
              {
                path: ':id',
                lazy: lazyRebalance,
                handle: { breadcrumb: RebalanceBreadcrumb },
              },
            ],
          },
        ],
      },
      {
        path: 'rebalanceReport',
        lazy: lazyRebalanceReport,
        handle: { breadcrumb: 'navMenu:menu.rebalanceReport' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.RebalanceReport),
      },
      {
        path: 'portfolio-trading-prototype',
        lazy: lazyPortfolioTradingHub,
        handle: { breadcrumb: 'portfolioTradingHub:title' },
        enabled: ({ featureFlags }) =>
          featureFlags[Flags.portfolio_trading_prototype].enabled,
      },
      {
        path: 'portfolio-trading-prototype-v2',
        lazy: lazyPortfolioTradingHubV2,
        handle: { breadcrumb: 'portfolioTradingHub:titleV2' },
        enabled: ({ featureFlags }) =>
          featureFlags[Flags.portfolio_trading_prototype].enabled,
      },
      {
        path: 'portfolioOptimizers',
        handle: { breadcrumb: 'navMenu:menu.portfolioOptimizer' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioOptimizer),
        children: [
          { index: true, lazy: lazyPortfolioOptimizers },
          {
            path: ':id',
            lazy: lazyPortfolioOptimizer,
            handle: { breadcrumb: PortfolioOptimizerBreadcrumb },
          },
        ],
      },
      {
        path: 'rebalance',
        element: createElement(
          BulkRebalanceRouteGuard,
          null,
          createElement(Outlet),
        ),
        handle: { breadcrumb: 'navMenu:menu.bulkRebalance' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioOptimizer),
        children: [
          { index: true, lazy: lazyBulkRebalance },
          {
            path: ':runId',
            lazy: lazyBulkRebalanceDetail,
            handle: { breadcrumb: BulkRebalanceBreadcrumb },
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.BulkTrader),
        children: [
          {
            path: 'bulkTrader',
            handle: { breadcrumb: 'navMenu:menu.bulkTrader' },
            children: [
              { index: true, lazy: lazyBulkTraders },
              {
                path: ':id',
                lazy: lazyBulkTrader,
                handle: { breadcrumb: BulkTraderBreadcrumb },
              },
            ],
          },
          {
            path: 'bulkTraderRevamp',
            handle: { breadcrumb: 'navMenu:menu.bulkTraderRevamp' },
            enabled: ({ featureFlags }) =>
              featureFlags[Flags.bulk_trader_revamp].enabled,
            children: [
              { index: true, lazy: lazyBulkTradersRevamp },
              {
                path: ':id',
                lazy: lazyBulkTraderRevamp,
                handle: { breadcrumb: BulkTraderRevampBreadcrumb },
              },
            ],
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.BulkImport),
        children: [
          {
            path: 'bulkImport',
            handle: { breadcrumb: 'navMenu:menu.bulkImport' },
            children: [
              { index: true, lazy: lazyBulkImports },
              {
                path: ':bulkImportId',
                handle: { breadcrumb: BulkImportBreadcrumb },
                children: [
                  { index: true, lazy: lazyBulkImport },
                  {
                    path: 'file/:importFileId',
                    lazy: lazyBulkImportFileLineItems,
                    handle: { breadcrumb: ImportFileBreadcrumb },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'elderlyClients',
        lazy: lazyElderlyClients,
        handle: { breadcrumb: 'navMenu:menu.elderlyClients' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ElderlyClients),
      },
      {
        path: 'transactionMonitoringTool',
        lazy: lazyTransactionMonitoring,
        handle: { breadcrumb: 'navMenu:menu.transactionMonitoringTool' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.SuspiciousTransactions),
      },
      {
        path: 'bankAccountsToReview',
        lazy: lazyBankAccountsToReview,
        handle: { breadcrumb: 'navMenu:menu.bankAccountsToReview' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BankAccountsToReview),
      },
      {
        path: 'institutionsToReview',
        lazy: lazyInstitutionsToReview,
        handle: { breadcrumb: 'navMenu:menu.institutionsToReview' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.InstitutionsToReview),
      },
      {
        path: 'annualSuitabilityReview',
        lazy: lazyAnnualSuitabilityReview,
        handle: { breadcrumb: 'navMenu:menu.annualSuitabilityReview' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AnnualSuitabilityReview),
      },
      {
        path: 'complianceReview',
        lazy: lazyComplianceReview,
        handle: { breadcrumb: 'navMenu:menu.complianceReview' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ComplianceReview),
      },
      {
        path: 'reconciliation',
        lazy: lazyReconciliation,
        handle: { breadcrumb: 'navMenu:menu.reconciliation' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Reconciliation),
      },
      {
        path: 'reconciliationActivities',
        lazy: lazyReconActivities,
        handle: { breadcrumb: 'navMenu:menu.activities' },
        enabled: ({ accessiblePages, featureFlags }) =>
          accessiblePages.has(Pages.Reconciliation) &&
          featureFlags[Flags.recon_activities_dashboard].enabled,
      },
      {
        path: 'flags',
        handle: { breadcrumb: 'navMenu:menu.flags' },
        enabled: ({ accessiblePages, featureFlags }) =>
          accessiblePages.has(Pages.Reconciliation) &&
          featureFlags[Flags.recon_flags_dashboard].enabled,
        children: [
          { index: true, lazy: lazyReconFlags },
          { path: 'glossary', lazy: lazyFlagGlossary },
        ],
      },
      {
        path: 'reconciliationV2',
        handle: { breadcrumb: 'navMenu:menu.reconciliation' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Reconciliation),
        children: [
          { index: true, lazy: lazyReconciliationV2 },
          {
            path: ':id',
            lazy: lazyAccountSnapshot,
            handle: { breadcrumb: AccountSnapshotBreadcrumb },
          },
        ],
      },
      {
        path: 'statementsReview',
        lazy: lazyStatementsReview,
        handle: { breadcrumb: 'navMenu:menu.statementsReview' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.StatementsReview),
      },
      {
        path: 'transactions',
        lazy: lazyAllTransactions,
        handle: { breadcrumb: 'navMenu:menu.transactions' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Transactions),
      },
      {
        path: 'accountsReview',
        lazy: lazyAccountsReview,
        handle: { breadcrumb: 'navMenu:menu.accountsReview' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AccountsReview),
      },
      {
        path: 'feeGrids',
        handle: { breadcrumb: 'navMenu:menu.feeGrids' },
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.FeeGrids),
        children: [
          { index: true, lazy: lazyFeeGrids },
          { path: 'new', lazy: lazyCreateFeeGridPage },
          {
            path: ':id',
            lazy: lazyFeeGridPage,
            handle: { breadcrumb: FeeGridBreadcrumb },
          },
        ],
      },
      {
        path: 'billingSchedules',
        handle: { breadcrumb: 'navMenu:menu.billingSchedules' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BillingSchedules),
        children: [
          { index: true, lazy: lazyBillingSchedules },
          { path: 'new', lazy: lazyCreateBillingSchedulePage },
          {
            path: ':id',
            lazy: lazyBillingSchedulePage,
            handle: { breadcrumb: BillingScheduleBreadcrumb },
          },
        ],
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BillingManagement),
        children: [
          {
            path: 'billingManagement',
            handle: { breadcrumb: 'navMenu:menu.incurredFees' },
            children: [
              { index: true, lazy: lazyBillingManagement },
              {
                path: ':billingCycleId',
                handle: { breadcrumb: FeeReportBreadcrumb },
                children: [
                  { index: true, lazy: lazyFeeReport },
                  {
                    path: 'subAccount/:subAccountId',
                    lazy: lazyDailyFeeDetails,
                    handle: { breadcrumb: DailyFeeDetailsBreadcrumb },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'orgSettings',
        handle: { breadcrumb: 'navMenu:menu.orgSettings' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.OrganizationSettings),
        children: [
          { index: true, lazy: lazyOrgSettings },
          {
            path: 'configureWorkflow/:id',
            handle: { breadcrumb: WorkflowBreadcrumb },
            lazy: lazyConfigureWorkflowPage,
          },
          {
            path: 'pageConfiguration/:id',
            handle: { breadcrumb: PageConfigurationBreadcrumb },
            lazy: lazyPageConfiguration,
          },
          {
            path: 'staticPageWidgetConfiguration/:id',
            handle: {
              breadcrumb: StaticPageWidgetConfigurationBreadcrumb,
            },
            lazy: lazyStaticPageWidgetConfiguration,
          },
          {
            path: 'clientReportTemplate/:id',
            handle: { breadcrumb: ClientReportTemplateBreadcrumb },
            lazy: lazyClientReportTemplate,
          },
          {
            path: 'orgDetails/:id',
            handle: { breadcrumb: OrgDetailsBreadcrumb },
            lazy: lazyOrgDetails,
          },
          // Kept for backward compatibility with orgSettings bookmarks/deep links.
          {
            path: 'editDashboard/:dashboardId',
            handle: { breadcrumb: DashboardBreadcrumb },
            lazy: lazyEditDashboard,
          },
          {
            path: 'formTemplate/:id',
            handle: { breadcrumb: FormTemplateBreadcrumb },
            lazy: lazyEditFormTemplate,
          },
        ],
      },
      {
        path: 'newsAndInsights',
        handle: { breadcrumb: 'navMenu:menu.newsAndInsights' },
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.NewsAndInsight),
        children: [{ index: true, lazy: lazyManageNewsAndInsight }],
      },
      {
        children: [
          {
            path: 'configurableTablePage/:configurableTablePageId',
            handle: { breadcrumb: ConfigurableTablePageBreadcrumb },
            children: [
              { index: true, lazy: lazyConfigurableTablePage },
              {
                path: 'accounts/:accountId',
                handle: { breadcrumb: AccountBreadcrumb },
                children: [
                  { index: true, lazy: lazyAccountsDetail },
                  {
                    path: 'allDocuments',
                    lazy: lazyStaticDocumentsPage,
                    handle: { breadcrumb: AllDocumentsBreadcrumb },
                    enabled: ({ featureFlags }) =>
                      featureFlags[Flags.mackenzie_release_3].enabled,
                  },
                ],
              },
              {
                path: 'accountGroups/:accountGroupId',
                lazy: lazyAccountGroupsDetail,
                handle: { breadcrumb: AccountGroupBreadcrumb },
              },
              {
                path: 'goals/:goalId',
                lazy: lazyGoalsDetail,
                handle: { breadcrumb: GoalBreadcrumb },
              },
              {
                path: 'households/:clientGroupId',
                handle: { breadcrumb: ClientGroupBreadcrumb },
                children: [
                  { index: true, lazy: lazyHouseHold },
                  accountGroupRoutes(),
                  {
                    path: 'account/:accountId',
                    lazy: lazyAccountsDetail,
                    handle: { breadcrumb: AccountBreadcrumb },
                  },
                  {
                    path: 'client/:userId',
                    handle: { breadcrumb: ClientBreadcrumb },
                    children: [
                      { index: true, lazy: lazyClientV2 },
                      {
                        path: 'account/:accountId',
                        lazy: lazyAccountsDetail,
                        handle: { breadcrumb: AccountBreadcrumb },
                      },
                    ],
                  },
                ],
              },
              {
                path: 'clients/:userId',
                handle: { breadcrumb: ClientBreadcrumb },
                children: [
                  { index: true, lazy: lazyClientV2 },
                  {
                    path: 'account/:accountId',
                    lazy: lazyAccountsDetail,
                    handle: { breadcrumb: AccountBreadcrumb },
                  },
                ],
              },
              {
                path: 'nonIndividualClients/:userId',
                handle: { breadcrumb: EntityBreadcrumb },
                children: [
                  { index: true, lazy: lazyNonIndividuals },
                  {
                    path: 'account/:accountId',
                    lazy: lazyAccountsDetail,
                    handle: { breadcrumb: AccountBreadcrumb },
                  },
                ],
              },
              {
                path: 'subAccounts/:subAccountId',
                lazy: lazySubAccountDetail,
                handle: { breadcrumb: SubAccountBreadcrumb },
              },
            ],
          },
        ],
      },
      // TODO: Temp path for testing
      { path: 'viewPage/:id/:objectId', lazy: lazyViewPage },
      {
        path: 'viewClientReport/:clientReportTemplateId/:objectId',
        lazy: lazyViewClientReport,
      },
      {
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.DeveloperSettings),
        children: [
          {
            path: 'devSettings',
            lazy: lazyDevSettings,
            handle: { breadcrumb: 'navMenu:menu.devSettings' },
          },
          {
            path: 'bulkUpsertRequests',
            handle: { breadcrumb: 'navMenu:menu.bulkUpsertRequests' },
            children: [
              { index: true, lazy: lazyBulkUpsertRequests },
              {
                path: ':bulkUpsertRequestId',
                lazy: lazyBulkUpsertItems,
                handle: { breadcrumb: BulkUpsertItemsBreadcrumb },
              },
            ],
          },
        ],
      },
      {
        path: 'reports',
        handle: { breadcrumb: 'navMenu:menu.reports' },
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Reports),
        children: [
          { index: true, lazy: lazyReports },
          {
            path: 'new',
            lazy: lazyNewReport,
            handle: { breadcrumb: 'customReports:newReport' },
          },
          {
            path: ':id',
            lazy: lazyViewReport,
            handle: { breadcrumb: CustomReportBreadcrumb },
          },
        ],
      },
      {
        path: 'workflows',
        lazy: lazyWorkflows,
        handle: { breadcrumb: 'navMenu:menu.workflows' },
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Workflows),
      },
      { path: 'refinitivShowcase', lazy: lazyRefinitivShowcase },
      {
        path: 'advisorContact',
        lazy: lazyAdvisorContactAdmin,
        handle: { breadcrumb: 'navMenu:menu.advisorContact' },
        enabled: ({ featureFlags }) =>
          featureFlags[Flags.advisor_contact_repcode].enabled,
      },
      {
        path: 'settings',
        children: [
          { index: true, element: navigateTo('/settings/general') },
          {
            path: 'general',
            lazy: lazyGeneralSettings,
            handle: { breadcrumb: 'navMenu:menu.general' },
          },
          {
            path: 'designer',
            lazy: lazyDesignerSettings,
            handle: { breadcrumb: 'navMenu:menu.designer' },
          },
          {
            path: 'textDesigner',
            lazy: lazyTextDesignerSettings,
            handle: { breadcrumb: 'navMenu:menu.textDesigner' },
            enabled: ({ featureFlags }) =>
              featureFlags[Flags.text_variants].enabled,
          },
          {
            path: 'authenticationTheme',
            lazy: lazyAuthenticationDesignerSettings,
            handle: { breadcrumb: 'navMenu:menu.authenticationTheme' },
          },
          {
            path: 'errorTheme',
            lazy: lazyErrorPageDesignerSettings,
            handle: { breadcrumb: 'navMenu:menu.errorTheme' },
          },
          {
            path: 'accessUsers',
            lazy: lazyAccessUsersSettings,
            handle: { breadcrumb: 'navMenu:menu.accessUsers' },
          },
          {
            path: 'authenticationSettings',
            lazy: lazyAuthenticationSettings,
            handle: { breadcrumb: 'navMenu:menu.authentication' },
          },
          {
            path: 'accessSettings',
            lazy: lazyAccessSettings,
            handle: { breadcrumb: 'navMenu:menu.accessSettings' },
          },
          {
            path: 'productExperience',
            lazy: lazyGeneralProductExperienceSettings,
            handle: { breadcrumb: 'navMenu:menu.generalProductExperience' },
          },
          {
            path: 'dashboards',
            handle: { breadcrumb: 'navMenu:menu.dashboards' },
            children: [
              { index: true, lazy: lazyDashboards },
              {
                path: 'editDashboard/:dashboardId',
                handle: { breadcrumb: DashboardBreadcrumb },
                lazy: lazyEditDashboard,
              },
            ],
          },
          {
            path: 'localizations',
            lazy: lazyLocalizationSettings,
            handle: { breadcrumb: 'navMenu:menu.localization' },
          },
          {
            path: 'localizationConfiguration',
            lazy: lazyLocalizationConfigurationSettings,
            handle: { breadcrumb: 'navMenu:menu.localizationConfiguration' },
          },
          {
            path: 'notifications',
            lazy: lazyNotifications,
            handle: { breadcrumb: 'navMenu:menu.notifications' },
          },
          {
            path: 'alertBanners',
            handle: { breadcrumb: 'navMenu:menu.alertBanners' },
            enabled: ({ featureFlags }) =>
              featureFlags[Flags.alert_banners].enabled,
            children: [
              { index: true, lazy: lazyAlertBanners },
              {
                path: 'new',
                lazy: lazyNewAlertBanner,
                handle: { breadcrumb: 'alertBanners:newBanner' },
              },
              {
                path: ':id',
                lazy: lazyEditAlertBanner,
                handle: { breadcrumb: AlertBannerBreadcrumb },
              },
            ],
          },
          {
            path: 'forms',
            lazy: lazyForms,
            handle: { breadcrumb: 'navMenu:menu.forms' },
          },
          {
            path: 'clientReports',
            handle: { breadcrumb: 'navMenu:menu.clientReports' },
            children: [
              { index: true, lazy: lazyClientReports },
              {
                path: ':id',
                handle: { breadcrumb: ClientReportTemplateBreadcrumb },
                lazy: lazyClientReportTemplate,
              },
            ],
          },
          {
            path: 'statementsAndTaxDocuments',
            lazy: lazyStatementsAndTaxDocumentsSettings,
            handle: { breadcrumb: 'navMenu:menu.statementsAndTaxDocuments' },
          },
          {
            path: 'customFields',
            lazy: lazyCustomFieldsSettings,
            handle: { breadcrumb: 'navMenu:menu.customFields' },
          },
          {
            path: 'repCodes',
            lazy: lazyRepCodesSettings,
            handle: { breadcrumb: 'navMenu:menu.repCodes' },
          },
          {
            path: 'customAttributeLabels',
            lazy: lazyCustomAttributeLabelsSettings,
            handle: { breadcrumb: 'navMenu:menu.customAttributeLabels' },
          },
          {
            path: 'pageConfigurations',
            handle: { breadcrumb: 'navMenu:menu.pageConfigurations' },
            children: [
              { index: true, lazy: lazyPageConfigurationsSettings },
              {
                path: ':id',
                handle: { breadcrumb: PageConfigurationBreadcrumb },
                lazy: lazyPageConfiguration,
              },
            ],
          },
          {
            path: 'homePageConfigurations',
            handle: { breadcrumb: 'navMenu:menu.homePageConfigurations' },
            children: [
              { index: true, lazy: lazyHomePageConfigurationsSettings },
              {
                path: ':homePageConfigurationId',
                handle: { breadcrumb: HomePageConfigurationBreadcrumb },
                lazy: lazyHomePageConfigurationEditor,
              },
            ],
          },
          {
            path: 'staticPageWidgetConfigurations',
            handle: {
              breadcrumb: 'navMenu:menu.staticPageWidgetConfigurations',
            },
            children: [
              { index: true, lazy: lazyStaticPageWidgetConfigurationsSettings },
              {
                path: ':id',
                handle: {
                  breadcrumb: StaticPageWidgetConfigurationBreadcrumb,
                },
                lazy: lazyStaticPageWidgetConfiguration,
              },
            ],
          },
          {
            path: 'configurableTablePages',
            handle: { breadcrumb: 'navMenu:menu.configurableTablePages' },
            children: [
              { index: true, lazy: lazyConfigurableTablePagesSettings },
              {
                path: ':configurableTablePageId',
                handle: { breadcrumb: ConfigurableTablePageBreadcrumb },
                lazy: lazyConfigurableTablePageEditor,
              },
            ],
          },
          {
            path: 'workflows',
            handle: { breadcrumb: 'navMenu:menu.workflows' },
            children: [
              { index: true, lazy: lazyWorkflowsSettings },
              {
                path: ':id',
                handle: { breadcrumb: WorkflowBreadcrumb },
                lazy: lazyConfigureWorkflowPage,
              },
            ],
          },
          {
            path: 'newsAndInsights',
            lazy: lazyNewsAndInsights,
            handle: { breadcrumb: 'navMenu:menu.newsAndInsights' },
          },
          {
            path: 'downtimeSchedule',
            lazy: lazyDowntimeSchedulesSettings,
            handle: { breadcrumb: 'navMenu:menu.downtimeSchedule' },
          },
          {
            path: 'transfers',
            lazy: lazyTransfersSettings,
            handle: { breadcrumb: 'navMenu:menu.transfers' },
          },
          {
            path: 'trades',
            lazy: lazyTradesSettings,
            handle: { breadcrumb: 'navMenu:menu.trades' },
          },
          {
            path: 'tradingSchedules',
            lazy: lazyTradingSchedulesSettings,
            handle: { breadcrumb: 'navMenu:menu.tradingSchedules' },
          },
          {
            path: 'assetClasses',
            lazy: lazyAssetClasses,
            handle: { breadcrumb: 'navMenu:menu.assetClasses' },
          },
          {
            path: 'compliance',
            lazy: lazyComplianceSettings,
            handle: { breadcrumb: 'navMenu:menu.compliance' },
          },
          {
            path: 'flagPolicies',
            lazy: lazyFlagPoliciesSettings,
            handle: { breadcrumb: 'navMenu:menu.flagPolicies' },
            enabled: ({ featureFlags }) =>
              featureFlags[Flags.recon_flags_dashboard].enabled,
          },
          {
            path: 'reconTolerance',
            lazy: lazyReconToleranceSettings,
            handle: { breadcrumb: 'navMenu:menu.reconTolerance' },
            enabled: ({ featureFlags }) =>
              featureFlags[Flags.recon_tolerance_config].enabled,
          },
          {
            path: 'portfolioManagementSettings',
            lazy: lazyPortfolioManagementSettings,
            handle: { breadcrumb: 'navMenu:menu.portfolioManagementSettings' },
          },
          {
            path: 'feeGrids',
            handle: { breadcrumb: 'navMenu:menu.feeGrids' },
            children: [
              { index: true, lazy: lazyFeeGridsSettings },
              {
                path: 'new',
                lazy: lazyCreateFeeGridPage,
                handle: { breadcrumb: 'settings:feeGrids.createTitle' },
              },
              {
                path: ':id',
                lazy: lazyFeeGridPage,
                handle: { breadcrumb: FeeGridBreadcrumb },
              },
            ],
          },
          {
            path: 'billingSchedules',
            handle: { breadcrumb: 'navMenu:menu.billingSchedules' },
            children: [
              { index: true, lazy: lazyBillingSchedulesSettings },
              {
                path: 'new',
                lazy: lazyCreateBillingSchedulePage,
                handle: { breadcrumb: 'settings:billingSchedules.createTitle' },
              },
              {
                path: ':id',
                lazy: lazyBillingSchedulePage,
                handle: { breadcrumb: BillingScheduleBreadcrumb },
              },
            ],
          },
          {
            path: 'salesTax',
            lazy: lazySalesTaxSettings,
            handle: { breadcrumb: 'navMenu:menu.salesTax' },
          },
          {
            path: 'feeSettings',
            lazy: lazyFeeSettings,
            handle: { breadcrumb: 'navMenu:menu.feeSettings' },
          },
          {
            path: 'apiTokens',
            lazy: lazyApiTokensSettings,
            handle: { breadcrumb: 'navMenu:menu.apiTokens' },
          },
          {
            path: 'webhooks',
            lazy: lazyWebhooksSettings,
            handle: { breadcrumb: 'navMenu:menu.webhooks' },
          },
          {
            path: 'siemDataFeed',
            lazy: lazySiemDataFeedSettings,
            handle: { breadcrumb: 'navMenu:menu.siemDataFeed' },
            enabled: ({ featureFlags }) =>
              featureFlags[Flags.siem_data_feed].enabled,
          },
          {
            path: 'embedded',
            lazy: lazyEmbeddedSettings,
            handle: { breadcrumb: 'navMenu:menu.embedded' },
          },
          {
            path: 'roleSettings',
            lazy: lazyRoleSettings,
            handle: { breadcrumb: 'navMenu:menu.roleSettings' },
          },
          {
            path: 'roleProfiles',
            lazy: lazyRoleProfilesSettings,
            handle: { breadcrumb: 'navMenu:menu.roleProfiles' },
          },
          {
            path: 'roles',
            lazy: lazyRolesSettings,
            handle: { breadcrumb: 'navMenu:menu.roles' },
          },
          {
            path: 'accounts',
            lazy: lazyAccountsTypesSettings,
            handle: { breadcrumb: 'navMenu:menu.accounts' },
          },
          {
            path: 'accountSettings',
            lazy: lazyAccountSettings,
            handle: { breadcrumb: 'navMenu:menu.accountSettings' },
          },
          {
            path: 'accountGroupSettings',
            lazy: lazyAccountGroupSettings,
            handle: { breadcrumb: 'navMenu:menu.accountGroupSettings' },
          },
          {
            path: 'custodianConnections',
            handle: { breadcrumb: 'navMenu:menu.custodianConnections' },
            children: [
              { index: true, lazy: lazyCustodianConnectionsSettings },
              {
                path: ':custodianConnectionId',
                lazy: lazyCustodianConnectionDetail,
                handle: { breadcrumb: CustodianConnectionBreadcrumb },
              },
            ],
          },
          {
            path: 'taxRankings',
            lazy: lazyTaxRankingsSettings,
            handle: { breadcrumb: 'navMenu:menu.taxRankings' },
          },
          {
            path: 'formDefinitions',
            lazy: lazyFormDefinitionsSettings,
            handle: { breadcrumb: 'navMenu:menu.formDefinitions' },
          },
          {
            path: 'formMapper',
            lazy: lazyFormMapper,
            handle: { breadcrumb: 'navMenu:menu.formMapper' },
          },
          {
            path: 'featureFlags',
            lazy: lazyFeatureFlagsSettings,
            handle: { breadcrumb: 'navMenu:menu.featureFlags' },
          },
          {
            path: 'integrations',
            handle: { breadcrumb: 'navMenu:menu.integrations' },
            children: [
              { index: true, lazy: lazyIntegrationSettings },
              {
                path: 'bulkUpsertRequests',
                handle: { breadcrumb: 'navMenu:menu.bulkUpsertRequests' },
                children: [
                  { index: true, lazy: lazyBulkUpsertRequests },
                  {
                    path: ':bulkUpsertRequestId',
                    lazy: lazyBulkUpsertItems,
                    handle: { breadcrumb: BulkUpsertItemsBreadcrumb },
                  },
                ],
              },
            ],
          },
          {
            children: [
              {
                path: 'bulkImport',
                handle: { breadcrumb: 'navMenu:menu.bulkImport' },
                children: [
                  { index: true, lazy: lazyBulkImportsSettings },
                  {
                    path: ':bulkImportId',
                    handle: { breadcrumb: BulkImportBreadcrumb },
                    children: [
                      { index: true, lazy: lazyBulkImportFilesSettings },
                      {
                        path: 'file/:importFileId',
                        lazy: lazyBulkImportFileLineItemsSettings,
                        handle: { breadcrumb: ImportFileBreadcrumb },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          { path: '*', element: navigateTo('/settings/general') },
        ],
      },
      { path: 'components', lazy: lazyComponentShowcase },
      { path: '*' },
    ],
  },
] satisfies AppRoute[];

// Root index and splat follow home-page config, not page access.
export const applyRuntimeFallback = (
  routes: RouteObject[],
  hasHomePage: boolean,
  fallbackRoute: ReactNode,
) =>
  routes.map((route) => {
    if (route.path !== '/' || route.children == null) {
      return route;
    }
    return {
      ...route,
      children: route.children.map((child) => {
        if (child.index) {
          if (hasHomePage) {
            return child;
          }
          return { index: true as const, element: fallbackRoute };
        }
        if (child.path === '*') {
          return { path: '*', element: fallbackRoute };
        }
        return child;
      }),
    };
  });
